import { h } from "snabbdom";
import type { VNode } from "snabbdom";
import type { Msg, ViewBlockDialog, ViewBlockForm, ViewExprField } from "../types";

export function viewViewBlockDialog(
  dialog: ViewBlockDialog,
  dispatch: (msg: Msg) => void
): VNode {
  const { form, errors } = dialog;
  const isInsert = dialog.mode === "insert";
  // Only the CEL fields actually shown for this view type can block saving.
  // (Kanban's "group field" is a plain tag name, not CEL, so it never does.)
  const vt = form.viewType;
  const relevant: ViewExprField[] = [
    "filter",
    ...(vt === "list" || vt === "kanban" ? (["orderBy"] as ViewExprField[]) : []),
    ...(vt === "network" ? (["groupBy"] as ViewExprField[]) : []),
  ];
  const hasErrors = relevant.some((k) => errors[k]);

  const setField = (key: keyof ViewBlockForm) => (e: Event) =>
    dispatch({
      type: "SetViewBlockField",
      key,
      value: (e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value,
    });

  const setCheckbox = (key: keyof ViewBlockForm) => (e: Event) =>
    dispatch({ type: "SetViewBlockField", key, value: String((e.target as HTMLInputElement).checked) });

  const textField = (key: keyof ViewBlockForm, label: string, placeholder: string, optional = true): VNode =>
    h("label.vb-label", [
      label,
      ...(optional ? [h("span.vb-hint", " (optional)")] : []),
      h("input.vb-input", {
        attrs: { name: key, placeholder, value: form[key] },
        on: { input: setField(key) },
      }),
    ]);

  // A CEL expression field: validated on the backend, with the error shown
  // inline. The invalid class is toggled via the class module (not the
  // selector) so the input keeps its identity — and the caret — while typing.
  const exprField = (key: ViewExprField, label: string, placeholder: string): VNode => {
    const error = errors[key];
    return h("label.vb-label", [
      label,
      h("span.vb-hint", " (CEL, optional)"),
      h("input.vb-input.vb-input--expr", {
        class: { "vb-input--invalid": !!error },
        attrs: { name: key, placeholder, value: form[key] },
        on: { input: setField(key) },
      }),
      ...(error ? [h("span.vb-error", error)] : []),
    ]);
  };

  const fields: VNode[] = [
    h("label.vb-label", [
      "Type",
      h(
        "select.vb-input",
        {
          attrs: { name: "viewType" },
          props: { value: form.viewType },
          on: { change: setField("viewType") },
          hook: { insert: (vn) => (vn.elm as HTMLSelectElement).focus() },
        },
        [
          h("option", { attrs: { value: "list", selected: form.viewType === "list" } }, "List"),
          h("option", { attrs: { value: "kanban", selected: form.viewType === "kanban" } }, "Kanban"),
          h("option", { attrs: { value: "network", selected: form.viewType === "network" } }, "Network"),
        ]
      ),
    ]),
    exprField("filter", "Filter", 'e.g. status == "todo"'),
  ];

  if (form.viewType === "list" || form.viewType === "kanban") {
    fields.push(exprField("orderBy", "Order by", "e.g. created_at"));
    const noOrder = !form.orderBy.trim();
    fields.push(
      h("label.vb-label", { class: { "vb-label--disabled": noOrder } }, [
        "Order direction",
        h(
          "select.vb-input",
          {
            attrs: { name: "orderDesc", disabled: noOrder },
            props: { value: form.orderDesc },
            on: { change: setField("orderDesc") },
          },
          [
            h("option", { attrs: { value: "true", selected: form.orderDesc !== "false" } }, "Descending"),
            h("option", { attrs: { value: "false", selected: form.orderDesc === "false" } }, "Ascending"),
          ]
        ),
      ])
    );
  }
  if (form.viewType === "kanban") {
    // Plain tag field (not CEL): keeps drag-drop reversible into a tag write.
    fields.push(textField("groupBy", "Group field", "e.g. status", false));
    fields.push(
      h("label.vb-label", [
        "Columns",
        h("span.vb-hint", " (optional, one per line — sets display order)"),
        h("textarea.vb-input.vb-textarea", {
          attrs: { name: "columns", placeholder: "TODO\nDoing\nDone", rows: 3, value: form.columns },
          props: { value: form.columns },
          on: { input: setField("columns") },
        }),
      ])
    );
    fields.push(
      h("label.vb-label.vb-label--inline", [
        h("input.vb-checkbox", {
          attrs: { name: "showAllColumns", type: "checkbox" },
          props: { checked: form.showAllColumns !== "false" },
          on: { change: setCheckbox("showAllColumns") },
        }),
        "Show columns beyond the list above",
      ])
    );
  }
  if (form.viewType === "list") {
    fields.push(
      h("label.vb-label", [
        "Display",
        h(
          "select.vb-input",
          { attrs: { name: "display" }, props: { value: form.display }, on: { change: setField("display") } },
          [
            h("option", { attrs: { value: "preview-short", selected: form.display === "preview-short" } }, "Preview (Short)"),
            h("option", { attrs: { value: "preview-medium", selected: form.display === "preview-medium" } }, "Preview (Medium)"),
            h("option", { attrs: { value: "preview-long", selected: form.display === "preview-long" } }, "Preview (Long)"),
            h("option", { attrs: { value: "full", selected: form.display === "full" } }, "Full"),
          ]
        ),
      ])
    );
  }
  if (form.viewType === "network") {
    fields.push(exprField("groupBy", "Group by", "e.g. status"));
  }

  return h(
    "div.vb-overlay",
    {
      on: {
        click: (e: Event) => {
          if ((e.target as HTMLElement).classList.contains("vb-overlay")) {
            dispatch({ type: "CloseViewBlockDialog" });
          }
        },
      },
    },
    [
      h("div.vb-dialog", [
        h("h3.vb-dialog__title", isInsert ? "Insert view" : "Edit view"),
        h(
          "form.vb-dialog__form",
          {
            on: {
              submit: (e: Event) => {
                e.preventDefault();
                if (!hasErrors) dispatch({ type: "CommitViewBlock" });
              },
            },
          },
          [
            ...fields,
            h("div.vb-dialog__actions", [
              h(
                "button.vb-btn.vb-btn--cancel",
                { attrs: { type: "button" }, on: { click: () => dispatch({ type: "CloseViewBlockDialog" }) } },
                "Cancel"
              ),
              h(
                "button.vb-btn.vb-btn--insert",
                { attrs: { type: "submit", disabled: hasErrors } },
                isInsert ? "Insert" : "Save"
              ),
            ]),
          ]
        ),
      ]),
    ]
  );
}
